import React, { useState, useEffect } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { masterMenuModuleService } from '../../../api/masterMenuModuleService';

/**
 * Create/Edit Screen Modal Component
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {function} onSubmit - Function to handle form submission
 * @param {boolean} loading - Loading state
 * @param {object} editingScreen - Screen data to edit (null for create mode)
 */
const CreateScreenModal = ({ isOpen, onClose, onSubmit, loading = false, editingScreen = null }) => {
  const [formData, setFormData] = useState({
    screenName: '',
    screenLable: '',
    masterMenuModuleId: '',
    routing: '',
    isActive: true,
    canView: true,
    canInsert: false,
    canUpdate: false,
    canDelete: false,
    orderNo: null,
    fafaIcon: '',
    isDashboard: false
  });
  const [errors, setErrors] = useState({});
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);

  // Fetch modules when modal opens and update form data when editingScreen changes
  useEffect(() => {
    if (isOpen) {
      loadModules();
    }
  }, [isOpen]);

  // Update form data when editingScreen changes
  useEffect(() => {
    if (editingScreen) {
      setFormData({
        screenName: editingScreen.screenName || '',
        screenLable: editingScreen.screenLable || editingScreen.screen_label || '',
        masterMenuModuleId: editingScreen.masterMenuModuleId || editingScreen.module_id || '',
        routing: editingScreen.routing || editingScreen.routePath || '',
        isActive: editingScreen.isActive !== undefined ? editingScreen.isActive : true,
        canView: editingScreen.canView !== undefined ? editingScreen.canView : true,
        canInsert: editingScreen.canInsert !== undefined ? editingScreen.canInsert : false,
        canUpdate: editingScreen.canUpdate !== undefined ? editingScreen.canUpdate : false,
        canDelete: editingScreen.canDelete !== undefined ? editingScreen.canDelete : false,
        orderNo: editingScreen.orderNo || editingScreen.order_no || null,
        fafaIcon: editingScreen.fafaIcon || editingScreen.fafa_icon || '',
        isDashboard: editingScreen.isDashboard !== undefined ? editingScreen.isDashboard : false
      });
    } else {
      setFormData({
        screenName: '',
        screenLable: '',
        masterMenuModuleId: '',
        routing: '',
        isActive: true,
        canView: true,
        canInsert: false,
        canUpdate: false,
        canDelete: false,
        orderNo: null,
        fafaIcon: '',
        isDashboard: false
      });
    }
    setErrors({});
  }, [editingScreen, isOpen]);

  const loadModules = async () => {
    try {
      setLoadingModules(true);
      // Load all modules (active and inactive) so users can assign screens to any module
      const response = await masterMenuModuleService.getModulesIncludingInactive();
      const modulesData = response?.data || [];
      setModules(modulesData);
    } catch (error) {
      console.error('Error loading modules:', error);
      setModules([]);
    } finally {
      setLoadingModules(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.screenName || formData.screenName.trim() === '') {
      newErrors.screenName = 'Screen name is required';
    }
    
    if (!formData.masterMenuModuleId || formData.masterMenuModuleId === '') {
      newErrors.masterMenuModuleId = 'Parent module is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Convert string values to proper types
    const submitData = {
      ...formData,
      masterMenuModuleId: parseInt(formData.masterMenuModuleId),
      orderNo: formData.orderNo ? parseInt(formData.orderNo) : null
    };
    
    onSubmit(submitData);
  };

  const handleClose = () => {
    setFormData({
      screenName: '',
      screenLable: '',
      masterMenuModuleId: '',
      routing: '',
      isActive: true,
      canView: true,
      canInsert: false,
      canUpdate: false,
      canDelete: false,
      orderNo: null,
      fafaIcon: '',
      isDashboard: false
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={editingScreen ? "Edit Screen" : "Create Screen"} size="xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="screenName" className="mb-2 block">
            Screen Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="screenName"
            type="text"
            placeholder="Enter screen name"
            value={formData.screenName}
            onChange={(e) => handleChange('screenName', e.target.value)}
            error={errors.screenName}
            required
            disabled={loading}
          />
        </div>

        <div>
          <Label htmlFor="screenLabel" className="mb-2 block">
            Screen Label
          </Label>
          <Input
            id="screenLabel"
            type="text"
            placeholder="Enter screen label"
            value={formData.screenLable}
            onChange={(e) => handleChange('screenLable', e.target.value)}
            error={errors.screenLable}
            disabled={loading}
          />
        </div>

        <div>
          <Label htmlFor="parentModule" className="mb-2 block">
            Parent Module <span className="text-red-500">*</span>
          </Label>
          <Select
            value={formData.masterMenuModuleId?.toString() || ''}
            onValueChange={(value) => handleChange('masterMenuModuleId', value)}
            disabled={loading || loadingModules}
          >
            <SelectTrigger>
              <SelectValue placeholder={loadingModules ? "Loading modules..." : "Select parent module"} />
            </SelectTrigger>
            <SelectContent>
              {modules.map((module) => (
                <SelectItem key={module.id} value={module.id?.toString()}>
                  {module.menumoduleName || module.moduleName || `Module ${module.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.masterMenuModuleId && (
            <p className="mt-1 text-sm text-[#BB0000]">{errors.masterMenuModuleId}</p>
          )}
        </div>

        <div>
          <Label htmlFor="routePath" className="mb-2 block">
            Route Path
          </Label>
          <Input
            id="routePath"
            type="text"
            placeholder="e.g., /admin/users"
            value={formData.routing}
            onChange={(e) => handleChange('routing', e.target.value)}
            error={errors.routing}
            disabled={loading}
          />
        </div>

        <div>
          <Label htmlFor="orderNo" className="mb-2 block">
            Order Number
          </Label>
          <Input
            id="orderNo"
            type="number"
            placeholder="Enter order number (optional)"
            value={formData.orderNo || ''}
            onChange={(e) => handleChange('orderNo', e.target.value ? parseInt(e.target.value) : null)}
            error={errors.orderNo}
            disabled={loading}
            min="0"
          />
        </div>

        <div>
          <Label htmlFor="fafaIcon" className="mb-2 block">
            Font Awesome Icon
          </Label>
          <Input
            id="fafaIcon"
            type="text"
            placeholder="e.g., fa-dashboard, fa-users"
            value={formData.fafaIcon}
            onChange={(e) => handleChange('fafaIcon', e.target.value)}
            error={errors.fafaIcon}
            disabled={loading}
          />
        </div>

        <div>
          <Label htmlFor="status" className="mb-2 block">
            Status
          </Label>
          <Select
            value={formData.isActive ? 'active' : 'inactive'}
            onValueChange={(value) => handleChange('isActive', value === 'active')}
            disabled={loading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="canView" className="mb-2 block">
              Can View
            </Label>
            <Select
              value={formData.canView ? 'yes' : 'no'}
              onValueChange={(value) => handleChange('canView', value === 'yes')}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="canInsert" className="mb-2 block">
              Can Insert
            </Label>
            <Select
              value={formData.canInsert ? 'yes' : 'no'}
              onValueChange={(value) => handleChange('canInsert', value === 'yes')}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="canUpdate" className="mb-2 block">
              Can Update
            </Label>
            <Select
              value={formData.canUpdate ? 'yes' : 'no'}
              onValueChange={(value) => handleChange('canUpdate', value === 'yes')}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="canDelete" className="mb-2 block">
              Can Delete
            </Label>
            <Select
              value={formData.canDelete ? 'yes' : 'no'}
              onValueChange={(value) => handleChange('canDelete', value === 'yes')}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="isDashboard" className="mb-2 block">
            Is Dashboard
          </Label>
          <Select
            value={formData.isDashboard ? 'yes' : 'no'}
            onValueChange={(value) => handleChange('isDashboard', value === 'yes')}
            disabled={loading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="gradient-primary text-primary-foreground"
            disabled={loading || loadingModules}
          >
            {loading ? (editingScreen ? 'Updating...' : 'Creating...') : (editingScreen ? 'Update Screen' : 'Create Screen')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateScreenModal;

